import { useAuth0 } from "@auth0/auth0-react";
import { PortfolioModel } from "@modelve/api/models/portfolioModels";
import { AxiosInstance } from "axios";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { create } from "zustand";
import { portfolioUrl } from "./apiRoutes";
import { createClient } from "./utils";

interface ApiStoreState {
  portfolioId?: string;
  tenantId?: string;
  accessToken?: string;
  client?: AxiosInstance;

  setRoute: (portfolioId?: string, tenantId?: string) => Promise<void>;
}

export const useApiStore = create<ApiStoreState>()((set, get) => ({
  setRoute: async (portfolioId, tenantId) => {
    if (!!portfolioId && !tenantId) {
      set({ portfolioId: portfolioId, tenantId: undefined });
      const client = get().client;
      if (client) {
        const data = await client.get<PortfolioModel>(
          `${portfolioUrl}/${portfolioId}`,
        );
        set({ tenantId: data.data.tenantId });
      }
    } else {
      set({ portfolioId: portfolioId, tenantId: tenantId });
    }
  },
}));

function useFetchToken() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();

  return useCallback(async () => {
    if (!isAuthenticated) {
      useApiStore.setState({ accessToken: undefined });
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      console.log("auth_0", "fetched new token");
      useApiStore.setState({ accessToken: token });
    } catch (error) {
      console.log("auth_0", "failed to fetch new token, requiring login");
      console.error(error);
      loginWithRedirect();
      useApiStore.setState({ accessToken: undefined });
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);
}

function useRefreshToken(onFetch: () => Promise<void>) {
  const token = useApiStore((state) => state.accessToken);

  useEffect(() => {
    if (!token) return;

    const decodedToken = jwtDecode(token);
    if (!decodedToken.exp) return;

    console.log(
      "auth_0",
      `refreshing token in ${Math.floor((decodedToken.exp * 1000 - new Date().getTime() - 59_000) / 1000)}`,
    );

    let refreshing = false;

    const interval = setInterval(async () => {
      if (!decodedToken.exp || refreshing) return;
      const isStale = decodedToken.exp * 1000 - new Date().getTime() <= 59_000;
      const isExpired = decodedToken.exp * 1000 - new Date().getTime() <= 0;

      if (isExpired) {
        useApiStore.setState({ accessToken: undefined });
      }

      if (isStale) {
        console.log("auth_0", "triggering refresh");
        refreshing = true;
        await onFetch();
      }
    }, 1_000);

    return () => clearInterval(interval);
  }, [token, onFetch]);
}

function useInitializeStore() {
  const { tenantId, portfolioId } = useParams();
  const token = useApiStore((state) => state.accessToken);

  const client = useMemo(
    () => (token ? createClient(token) : undefined),
    [token],
  );

  useEffect(() => useApiStore.setState({ client: client }), [client]);
  useEffect(() => {
    useApiStore.getState().setRoute(portfolioId, tenantId);
  }, [portfolioId, tenantId, client]);
}

export function useApiProvider() {
  const { isAuthenticated } = useAuth0();
  const onFetchToken = useFetchToken();

  useEffect(() => {
    onFetchToken();
  }, [isAuthenticated, onFetchToken]);

  useRefreshToken(onFetchToken);
  useInitializeStore();
}
